import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { EFBStaleUnit } from "@vmosolutions/oc-interfaces";
import { IStaleThreshold } from "./deviceGridHelpers";

interface IStaleDeviceFilterProps {
  /** Applies (or clears) the server-side threshold filter. */
  onFind: (threshold: IStaleThreshold | null) => void;
  grouped: boolean;
  onGroupedChange: (grouped: boolean) => void;
  /** Right-aligned result summary, e.g. "37 devices not active in the last 3 months". */
  resultSummary: string;
  disabled: boolean;
  memberNoun: string;
}

const UNIT_LABELS: Record<EFBStaleUnit, string> = {
  [EFBStaleUnit.Days]: "Days",
  [EFBStaleUnit.Weeks]: "Weeks",
  [EFBStaleUnit.Months]: "Months",
  [EFBStaleUnit.Years]: "Years",
};

/**
 * The "Find stale devices: not active in more than [ N ] [ unit ]" control.
 *
 * The threshold is applied server-side rather than by filtering an already
 * fetched list: at fleet scale (over a thousand crew devices for the largest
 * customer) the client should not have to hold every row to answer the
 * question, and "Select all matching" is only truthful if the server returned
 * exactly the matching set.
 */
export const StaleDeviceFilter = (props: IStaleDeviceFilterProps): JSX.Element => {
  const { onFind, grouped, onGroupedChange, resultSummary, disabled, memberNoun } = props;
  const theme = useTheme();
  const [amount, setAmount] = useState<string>("3");
  const [unit, setUnit] = useState<EFBStaleUnit>(EFBStaleUnit.Months);

  const parsed = Number(amount);
  const amountValid = Number.isFinite(parsed) && parsed > 0;

  const inputSx = {
    background: theme.custom.forms.inputBack,
    border: `1px solid ${theme.custom.forms.inputBorder}`,
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        mb: 1,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Find stale devices:
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.75 }}>
        not active in more than
      </Typography>
      <TextField
        size="small"
        type="number"
        value={amount}
        disabled={disabled}
        onChange={(e) => setAmount(e.target.value)}
        error={amount !== "" && !amountValid}
        inputProps={{ min: 1, "aria-label": "Inactivity threshold amount" }}
        sx={{ ...inputSx, width: 90 }}
      />
      <Select
        size="small"
        value={unit}
        disabled={disabled}
        onChange={(e) => setUnit(e.target.value as EFBStaleUnit)}
        inputProps={{ "aria-label": "Inactivity threshold unit" }}
        sx={{ ...inputSx, minWidth: 110 }}
      >
        {Object.values(EFBStaleUnit).map((u) => (
          <MenuItem key={u} value={u}>
            {UNIT_LABELS[u]}
          </MenuItem>
        ))}
      </Select>
      <Button
        variant="contained"
        color="primary"
        size="small"
        disabled={disabled || !amountValid}
        onClick={() => onFind({ amount: parsed, unit })}
      >
        Find
      </Button>
      <Button variant="text" size="small" disabled={disabled} onClick={() => onFind(null)}>
        Show all
      </Button>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={grouped}
            disabled={disabled}
            onChange={(e) => onGroupedChange(e.target.checked)}
          />
        }
        label={<Typography variant="body2">Group duplicates by {memberNoun}</Typography>}
        sx={{ ml: 1 }}
      />
      <Box sx={{ flexGrow: 1 }} />
      <Typography variant="body2" sx={{ opacity: 0.75 }} data-testid="device-result-summary">
        {resultSummary}
      </Typography>
    </Box>
  );
};
